import * as EmailValidator from 'email-validator'
import * as Sentry from '@sentry/react'
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Toast from '@radix-ui/react-toast'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import {
  ErrorBlockText,
  LeftAlignSmallText,
  TextInput,
  TocSmallText,
} from '@/components/widget-components'
import { usePageStateStore, useSignupStore } from '@/reducers'
import { usePasswordlessAuth } from '@/hooks/usePasswordlessAuth'
import { resolveSignupVendorId } from '@/utils/vendorId'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
  trackOnboardingVerificationCodeResent,
} from '@/utils/analytics'

const REFERRAL_PARAM = 'ref'

const readQueryParam = (key: string): string | null => {
  try {
    return new URLSearchParams(window.location.search).get(key)
  } catch {
    return null
  }
}

export default function SignupContentEmail() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const setEmailInStore = useSignupStore.use.setEmail()
  const setReferralCodeInStore = useSignupStore.use.setReferralCode()
  const setAuthToken = usePageStateStore.use.setAuthToken()

  const [email, setEmail] = useState('')
  const [referralCode, setReferralCode] = useState('')
  const [showReferral, setShowReferral] = useState(false)
  const [inputError, setInputError] = useState('')
  const [resendToastOpen, setResendToastOpen] = useState(false)

  useEffect(() => {
    trackOnboardingStepViewed('email_entry')
  }, [])

  // Sync URL params into localStorage so subsequent signup steps can read
  // them (phone, personal info, address, etc.). Also pre-fill the email and
  // referral inputs on this step.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const passthrough: Array<[string, string]> = [
      ['leftSideValue', 'leftSideValue'],
      ['leftSideLabel', 'leftSideLabel'],
      ['rightSideLabel', 'rightSideLabel'],
      ['email', 'email'],
      ['ref', 'ref'],
      ['phone', 'phone'],
      ['firstName', 'firstName'],
      ['lastName', 'lastName'],
      ['dob1', 'dob1'],
      ['dob2', 'dob2'],
      ['dob3', 'dob3'],
      ['address1', 'address1'],
      ['address2', 'address2'],
      ['city', 'city'],
      ['province', 'province'],
      ['postal', 'postal'],
    ]
    for (const [param, storageKey] of passthrough) {
      const value = urlParams.get(param)
      if (value !== null) {
        localStorage.setItem(storageKey, value)
      }
    }

    setEmail(localStorage.getItem('email') ?? '')

    const referralFromUrlOrStorage =
      urlParams.get(REFERRAL_PARAM) ?? localStorage.getItem('ref') ?? ''
    if (referralFromUrlOrStorage) {
      setShowReferral(true)
      setReferralCode(referralFromUrlOrStorage)
    }
  }, [])

  const errorMessages = useMemo(
    () => ({
      sendFailed: t('signup.email.error.unexpected'),
      timedOut: t('login.errors.signInTimedOut'),
      unexpected: t('login.errors.unexpectedError'),
    }),
    [t]
  )

  const onVerified = useCallback(
    ({
      email: verifiedEmail,
      accessToken,
    }: {
      email: string
      accessToken: string
    }) => {
      setEmailInStore(verifiedEmail)
      setReferralCodeInStore(referralCode)
      setAuthToken(accessToken)
      trackOnboardingStepCompleted('external_email_verify')
      navigate('/', { replace: true })
    },
    [
      navigate,
      referralCode,
      setAuthToken,
      setEmailInStore,
      setReferralCodeInStore,
    ]
  )

  const {
    sendMagicLink,
    waitingForApproval,
    denied,
    loading,
    errorMessage,
    lastSendSucceeded,
    resetDenied,
  } = usePasswordlessAuth({
    client: 'widget',
    sentryComponent: 'SignupContentEmail',
    errorMessages,
    onVerified,
  })

  const prevWaitingForApprovalRef = useRef(false)
  useEffect(() => {
    if (waitingForApproval && !prevWaitingForApprovalRef.current) {
      trackOnboardingStepCompleted('email_entry')
      trackOnboardingStepViewed('external_email_verify')
    }
    prevWaitingForApprovalRef.current = waitingForApproval
  }, [waitingForApproval])

  const prevDeniedRef = useRef(false)
  useEffect(() => {
    if (denied && !prevDeniedRef.current) {
      trackOnboardingStepFailed('external_email_verify', 'magic_link_denied')
    }
    prevDeniedRef.current = denied
  }, [denied])

  const updateReferral = useCallback(() => setShowReferral((v) => !v), [])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()

    trackOnboardingStepSubmitted('email_entry')

    Sentry.addBreadcrumb({
      category: 'signup',
      message: 'Email signup form submitted',
      level: 'info',
      data: { hasReferralCode: !!referralCode },
    })

    if (!EmailValidator.validate(email)) {
      setInputError(t('signup.email.error.invalid'))
      trackOnboardingStepFailed('email_entry', 'invalid_email')
      return
    }
    setInputError('')

    localStorage.setItem('email', email)
    if (referralCode) {
      localStorage.setItem('ref', referralCode)
    }
    try {
      window.clarity?.('set', 'email', email)
    } catch (clarityErr) {
      Sentry.captureException(clarityErr)
    }

    await sendMagicLink({
      email,
      referral: referralCode.trim() || undefined,
      vendor: resolveSignupVendorId(),
    })
  }

  if (loading && !waitingForApproval) {
    return (
      <div className="my-auto flex justify-center">
        <LoadingIcon />
      </div>
    )
  }

  if (denied) {
    return (
      <div className="flex flex-col gap-5">
        <div className="text-lg font-normal uppercase">
          {t('login.denied.title')}
        </div>
        <div className="text-base">{t('login.denied.status')}</div>
        <Button
          className="inline-flex items-center py-8 px-4 w-full justify-center"
          onClick={resetDenied}
        >
          {t('login.denied.tryAgain')}
        </Button>
      </div>
    )
  }

  if (waitingForApproval) {
    return (
      <div className="flex flex-col gap-5">
        <div className="text-lg font-normal uppercase">
          {t('login.checkEmail.title')}
        </div>
        <div className="text-base" data-testid="text-check-email">
          {t('login.checkEmail.sentLink', { email })}
        </div>
        <div className="text-base" data-testid="text-keep-page-open">
          {t('login.checkEmail.keepPageOpen')}
        </div>
        <LeftAlignSmallText className="mt-4">
          <Toast.Provider swipeDirection="right">
            <button
              className="codeButton"
              type="button"
              style={{ float: 'left', textDecoration: 'underline' }}
              onClick={async () => {
                trackOnboardingVerificationCodeResent('email')
                await sendMagicLink({
                  email,
                  referral: referralCode.trim() || undefined,
                  vendor: resolveSignupVendorId(),
                })
                setResendToastOpen(true)
              }}
              data-testid="btn-resend-link"
            >
              {t('login.resendLink')}
            </button>
            <Toast.Root
              className="ToastRoot"
              open={resendToastOpen}
              onOpenChange={setResendToastOpen}
            >
              {lastSendSucceeded && (
                <Toast.Title className="ToastTitle">
                  {t('login.toast.emailSent')}
                </Toast.Title>
              )}
              {!lastSendSucceeded && (
                <Toast.Title className="ToastTitle">
                  {t('login.toast.unableToSend')}
                </Toast.Title>
              )}
            </Toast.Root>
            <Toast.Viewport className="ToastViewport" />
          </Toast.Provider>
        </LeftAlignSmallText>
      </div>
    )
  }

  const displayedError = inputError || errorMessage

  return (
    <>
      <form className="flex flex-col gap-5" onSubmit={onSubmit}>
        <div className="text-lg font-normal uppercase">{t('signup.title')}</div>
        <div className="text-base">{t('signup.content')}</div>
        <TextInput
          name="email"
          aria-label="email"
          data-testId="input-form-email"
          placeholder={t('signup.email.placeholder')}
          type="email"
          onChange={(e) => setEmail(e.target.value)}
          value={email}
          autoFocus
        />
        <div className="mb-[-2rem]">
          <LeftAlignSmallText>
            <button
              className="hover:text-clementine dark:hover:text-sun"
              type="button"
              onClick={updateReferral}
              style={{ textDecoration: 'underline' }}
            >
              {t('signup.referral.content')}
            </button>
          </LeftAlignSmallText>
        </div>

        {showReferral && (
          <TextInput
            placeholder={t('signup.referral.placeholder')}
            name="ReferralCode"
            aria-label="referralCode"
            type="text"
            onChange={(e) => setReferralCode(e.target.value)}
            value={referralCode}
          />
        )}
        <div className="mb-2">
          <TocSmallText>
            {t('signup.toc.content')}
            <a
              href={import.meta.env.VITE_PAYTRIE_TOC_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
            >
              {t('signup.toc.content.1')}
            </a>{' '}
            {t('signup.toc.content.2')}
            <a
              href={import.meta.env.VITE_PAYTRIE_PRIVACY_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
            >
              {t('signup.toc.content.3')}
            </a>
            {t('signup.toc.content.4')}
            <a
              href={import.meta.env.VITE_KYC_TOC_LINK_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
            >
              {t('signup.toc.content.1')}
            </a>{' '}
            and{' '}
            <a
              href={import.meta.env.VITE_KYC_TOC_PRIVACY_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
            >
              {t('signup.toc.content.3')}
            </a>
            {t('signup.toc.content.6')}
          </TocSmallText>
        </div>
        <ErrorBlockText>{displayedError}</ErrorBlockText>
        <Button
          className="inline-flex items-center py-8 px-4 w-full justify-center lg:gap-2"
          data-testId="btn-continue"
          type="submit"
          disabled={!email.length}
        >
          {t('signup.submit')}
        </Button>

        <LeftAlignSmallText>
          {t('signup.hasAccount.text')}{' '}
          <button
            type="button"
            tabIndex={0}
            className="cursor-pointer underline hover:text-clementine dark:hover:text-sun outline-offset-4 focus:text-clementine dark:focus:text-sun"
            onClick={() => {
              navigate('/EasyLogin')
            }}
          >
            {t('signup.hasAccount.signInLink')}
          </button>
          .
        </LeftAlignSmallText>
      </form>
    </>
  )
}
